// BM-H3707 - the form a fee earner fills in to send an LPA questionnaire.
//
// This replaced three browser pop-up boxes (client email, your email, and a
// typed "hw or pf"). The typed type was read as `raw === "pf" ? "pf" : "hw"`, so
// "property", "financial" or "p&f" quietly produced a health and welfare
// questionnaire and reported success. Here the type is two clear choices with
// none pre-selected, and nothing can be sent until one is chosen.

import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

export const LPA_SEND_TYPES = [
  ["hw", "Health and welfare"],
  ["pf", "Property and financial affairs"],
];

// Deliberately plain: one @, something either side, a dot after it, no spaces.
export function looksLikeEmail(value) {
  const text = (value || "").trim();
  const at = text.indexOf("@");
  return at > 0 && text.indexOf(" ") < 0 && text.indexOf("@", at + 1) < 0
    && text.indexOf(".", at) > at + 1 && !text.endsWith(".");
}

// BM-H4003: the fee earner is the MATTER's fee earner, read from the matter by the page and passed in (feeEarnerEmail,
// feeEarnerName). It is shown, not typed: a typed name used to make whoever typed it the owner of the form.
// BM-H4256: the client's name is filled in from the matter and can be changed, like the email. It used to be fixed
// to the matter's lead client, so a second client on the same matter (a couple, Liezel's UAT on 13 Oct 2026) got an
// invitation greeting the first client, and staff saw the first client's name on both forms.
export default function LpaSendDialog({ open, onOpenChange, defaultClientEmail, defaultClientName, feeEarnerEmail, feeEarnerName, busy, onSend }) {
  const [kind, setKind] = useState("");
  const [clientEmail, setClientEmail] = useState(defaultClientEmail || "");
  const [clientName, setClientName] = useState(defaultClientName || "");

  useEffect(() => {
    if (!open) return;
    setKind("");
    setClientEmail(defaultClientEmail || "");
    setClientName(defaultClientName || "");
  }, [open, defaultClientEmail, defaultClientName]);

  const ready = Boolean(kind) && Boolean(clientName.trim()) && looksLikeEmail(clientEmail) && looksLikeEmail(feeEarnerEmail);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lpa-send-dialog">
        <DialogHeader>
          <DialogTitle>Send an LPA questionnaire</DialogTitle>
          <DialogDescription>
            Each LPA has its own questionnaire. For a health and welfare LPA and a property and
            financial LPA, send one of each.
          </DialogDescription>
        </DialogHeader>

        <fieldset className="space-y-2" data-testid="lpa-send-type">
          <legend className="text-sm font-semibold">Which LPA is this for?</legend>
          {LPA_SEND_TYPES.map(([value, label]) => (
            <label key={value} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="lpa-send-type"
                value={value}
                checked={kind === value}
                onChange={() => setKind(value)}
                data-testid={`lpa-send-type-${value}`}
              />
              {label}
            </label>
          ))}
        </fieldset>

        <label className="block text-sm font-semibold">
          Client name
          <Input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            data-testid="lpa-send-client-name"
          />
        </label>
        <label className="block text-sm font-semibold">
          Client email
          <Input
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            data-testid="lpa-send-client-email"
          />
        </label>
        <div className="block text-sm font-semibold">
          Sent in the name of, and from the mailbox of, the fee earner on this matter
          <p className="mt-1 font-normal" data-testid="lpa-send-fee-earner">
            {feeEarnerName ? `${feeEarnerName} (${feeEarnerEmail})` : feeEarnerEmail}
          </p>
        </div>
        {!kind && (
          <p className="text-xs text-slate-500" data-testid="lpa-send-hint">Choose which LPA this is for before you send.</p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="lpa-send-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!ready || busy}
            onClick={() => onSend({ lpa_kind: kind, client_name: clientName.trim(), client_email: clientEmail.trim(), fee_earner_email: feeEarnerEmail.trim() })}
            data-testid="lpa-send-submit"
          >
            Send questionnaire
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
